import {createContext, FormEvent, FormHTMLAttributes, ReactNode, useContext, useId} from "react";
import {OtsActionButton, OtsAsyncActionButton, OtsButtonVariant} from "./OtsButton";
import {OtsInlineAlert} from "./OtsInlineAlert";

export type OtsFormDensity = "compact" | "default";

const DensityContext = createContext<OtsFormDensity>("default");

/** Density of the enclosing OtsForm, so fields and sections can size themselves to match. */
export const useOtsFormDensity = () => useContext(DensityContext);

export type OtsFormProps = Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "className"> & {
  /** Accessible name; also what tests find the form by (`getByRole('form', {name})`). */
  "aria-label": string;
  /** Called with the native event after `preventDefault`, so FormData readers keep working. */
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  density?: OtsFormDensity;
  /** Disables every control while a save is in flight. */
  disabled?: boolean;
  /** Shown above the sections, e.g. an OtsFormErrorSummary. */
  alert?: ReactNode;
  /** Footer, usually OtsFormActions. */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
};

/**
 * Form shell: a container-query root so sections lay out by the space they actually get
 * (GWT panels, drawers, full pages), with a slot for errors above and actions below.
 */
export const OtsForm = ({
  onSubmit,
  density = "default",
  disabled = false,
  alert,
  actions,
  className,
  children,
  ...props
}: OtsFormProps) => (
  <DensityContext.Provider value={density}>
    <form
      {...props}
      onSubmit={event => {
        event.preventDefault();
        onSubmit(event);
      }}
      className={["@container", density === "compact" ? "space-y-3" : "space-y-4", className].filter(Boolean).join(" ")}>
      {alert}
      <fieldset disabled={disabled} className={[
        // Mantine's unlayered normalize CSS pads fieldsets; important utilities win over it.
        "!m-0 min-w-0 !border-0 !p-0", density === "compact" ? "space-y-3" : "space-y-4"].join(" ")}>
        {children}
      </fieldset>
      {actions}
    </form>
  </DensityContext.Provider>
);

export type OtsFormSectionProps = {
  title: ReactNode;
  description?: ReactNode;
  /** Header-right content, e.g. a section toggle. */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
};

/** A titled card grouping related fields; its body is its own container for OtsFormRow. */
export const OtsFormSection = ({title, description, actions, className, children}: OtsFormSectionProps) => {
  const density = useOtsFormDensity();
  const titleId = useId();
  const compact = density === "compact";
  return <section
    aria-labelledby={titleId}
    className={["min-w-0 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]", className].filter(Boolean).join(" ")}>
    <div className={[
      "flex flex-wrap items-start justify-between gap-3 border-b border-[var(--ots-outline-variant)]",
      compact ? "px-3 py-2.5" : "px-5 py-4",
    ].join(" ")}>
      <div className="min-w-0">
        <h2 id={titleId} className={[
          "font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-secondary)]",
          compact ? "text-sm" : "text-base",
        ].join(" ")}>{title}</h2>
        {description && <p className={["text-[var(--ots-text-muted)]", compact ? "mt-0.5 text-xs" : "mt-1 text-sm"].join(" ")}>{description}</p>}
      </div>
      {actions}
    </div>
    <div className={["@container", compact ? "space-y-3 p-3" : "space-y-4 p-5"].join(" ")}>
      {children}
    </div>
  </section>;
};

const ROW_COLUMNS = {
  2: "@md:grid-cols-2",
  3: "@md:grid-cols-2 @3xl:grid-cols-3",
  4: "@md:grid-cols-2 @3xl:grid-cols-4",
} as const;

export type OtsFormRowProps = {
  /** Columns once the section is wide enough; narrow containers always stack. */
  columns?: keyof typeof ROW_COLUMNS;
  className?: string;
  children: ReactNode;
};

/** Fields side by side when the section has room, stacked when it doesn't. */
export const OtsFormRow = ({columns = 2, className, children}: OtsFormRowProps) => {
  const density = useOtsFormDensity();
  return <div className={["grid min-w-0", density === "compact" ? "gap-3" : "gap-4", ROW_COLUMNS[columns], className].filter(Boolean).join(" ")}>
    {children}
  </div>;
};

export type OtsFormColumnsProps = {className?: string; children: ReactNode};

/** Places sections side by side on wide forms (e.g. "Basics" next to "Applies to"). */
export const OtsFormColumns = ({className, children}: OtsFormColumnsProps) => {
  const density = useOtsFormDensity();
  return <div className={["grid min-w-0 items-start @4xl:grid-cols-2", density === "compact" ? "gap-3" : "gap-4", className].filter(Boolean).join(" ")}>
    {children}
  </div>;
};

export type OtsFormErrorSummaryProps = {
  /** Field-keyed messages; nothing renders when it's empty. */
  errors: Record<string, string | undefined> | string[];
  title?: ReactNode;
};

/** "N fields need attention" banner shown after a failed submit. */
export const OtsFormErrorSummary = ({errors, title = "Check the form"}: OtsFormErrorSummaryProps) => {
  const count = (Array.isArray(errors) ? errors : Object.values(errors)).filter(Boolean).length;
  if (count === 0) return null;
  return <OtsInlineAlert tone="danger" title={title} size={useOtsFormDensity()}>
    {count === 1 ? "One field needs attention." : `${count} fields need attention.`}
  </OtsInlineAlert>;
};

export type OtsFormActionsProps = {
  submitLabel: ReactNode;
  submittingLabel?: ReactNode;
  submitIcon?: ReactNode;
  /** Accessible name of the spinner while submitting. */
  progressLabel?: string;
  submitting?: boolean;
  onCancel?: () => void;
  cancelLabel?: ReactNode;
  /** Left-aligned extras, e.g. a delete button or a hint. */
  start?: ReactNode;
  submitDisabled?: boolean;
  /** "destructive" for a submit that deletes or can't be undone. */
  submitVariant?: Extract<OtsButtonVariant, "primary" | "destructive">;
  /** Starts focus on Cancel, the safe choice when submitting is consequential. */
  autoFocusCancel?: boolean;
};

/** Cancel + primary submit, right-aligned, matching the form's density. */
export const OtsFormActions = ({
  submitLabel,
  submittingLabel,
  progressLabel,
  submitIcon,
  submitting = false,
  onCancel,
  cancelLabel = "Cancel",
  start,
  submitDisabled,
  submitVariant = "primary",
  autoFocusCancel = false,
}: OtsFormActionsProps) => {
  const size = useOtsFormDensity();
  return <div className="flex flex-wrap items-center gap-2">
    {start && <div className="mr-auto flex flex-wrap items-center gap-2">{start}</div>}
    <div className="ml-auto flex flex-wrap justify-end gap-2">
      {onCancel && <OtsActionButton size={size} onClick={onCancel} disabled={submitting}
        // Headless UI dialogs pick their initial focus by data-autofocus.
        autoFocus={autoFocusCancel || undefined} data-autofocus={autoFocusCancel || undefined}>{cancelLabel}</OtsActionButton>}
      <OtsAsyncActionButton
        type="submit"
        variant={submitVariant}
        size={size}
        isLoading={submitting}
        loadingLabel={submittingLabel}
        progressLabel={progressLabel}
        icon={submitIcon}
        disabled={submitDisabled}>
        {submitLabel}
      </OtsAsyncActionButton>
    </div>
  </div>;
};
